import React, { useEffect, useState } from "react"
import {
	FileText,
	Zap,
	Check,
	X,
	Minus,
	GitBranch,
	Bell,
	Send,
	Search,
	ChevronDown,
	ListChecks,
	Code,
	Info,
	Files,
	Calendar,
	MailOpen,
	Database,
	Layers,
	Building2,
	Flag,
	FileSearch,
	RotateCw,
} from "lucide-react"

import "./timeline.scss"

/**
 * Render-from-state timeline UI for multi-step agent workflows.
 *
 * Lifted out of the /design admin sandbox into a reusable component so it can
 * drive real flows (e.g. the supplier-document Process modal) as well as the
 * sandbox. The page renders purely from a `{ stages: [...] }` data object —
 * see docs/timeline-ui.md for the full field reference.
 *
 * Statuses understood per activity / group / item:
 *   pending | active | complete | failed | skipped
 * (`failed` and `skipped` are additive over the original sandbox, used by the
 * supplier pipeline where steps can error or be deliberately skipped.)
 */

/**
 * Icon registry — kept explicit so the JSON is safely stateable + we get
 * tree-shaken bundle output. Add new icons here as needed.
 */
const ICONS = {
	"file-text": FileText,
	zap: Zap,
	"git-branch": GitBranch,
	bell: Bell,
	send: Send,
	search: Search,
	"list-checks": ListChecks,
	code: Code,
	check: Check,
	info: Info,
	files: Files,
	calendar: Calendar,
	"mail-open": MailOpen,
	database: Database,
	layers: Layers,
	building: Building2,
	flag: Flag,
	"file-search": FileSearch,
	"rotate-cw": RotateCw,
}

function Icon({ name, ...rest }) {
	const Component = ICONS[name]
	if (!Component) return null
	return <Component {...rest} />
}

/**
 * Visual pip rendered to the left of each activity row + activity card header.
 * The element-type changes by status, so React naturally remounts and any
 * one-shot enter animation (e.g. the green check's scale-pop) plays cleanly.
 */
function StatusPip({ status }) {
	if (status === "complete") {
		return (
			<span className="timeline-check-pip">
				<Check
					size={12}
					strokeWidth={3}
					color="#fff" />
			</span>
		)
	}
	if (status === "failed") {
		return (
			<span className="timeline-x-pip">
				<X
					size={12}
					strokeWidth={3}
					color="#fff" />
			</span>
		)
	}
	if (status === "skipped") {
		return (
			<span className="timeline-skip-pip">
				<Minus
					size={12}
					strokeWidth={3}
					color="#fff" />
			</span>
		)
	}
	if (status === "active") return <span className="timeline-spinner" />
	return <span className="timeline-dashed-ring" />
}

function HeaderCard({ header }) {
	if (!header) return null
	return (
		<div className="timeline-card timeline-card--row">
			<div className="timeline-card-left">
				<div className="timeline-icon-tile">
					<Icon
						name={header.icon}
						size={15}
						strokeWidth={1.75}
						color="currentColor" />
				</div>
				<div className="timeline-card-title">{header.title}</div>
			</div>
			{header.action && (
				<button
					type="button"
					className="timeline-pill-button"
					disabled={header.action.disabled}
					onClick={header.action.onClick}>
					{header.action.label}
				</button>
			)}
		</div>
	)
}

/**
 * Renders a badge icon — chooses between the filled glyph treatment
 * (used for Zap so it reads as a solid bolt) and outline-stroke
 * treatment (Check, Info). Filling a stroke-only outline icon collapses
 * it into a solid shape (the bug that turned Info into a blue disk).
 */
function BadgeIcon({ name }) {
	if (!name) return null
	const filled = name === "zap"
	// Info is one pixel smaller than the check — the round outline reads
	// visually a touch heavier at the same nominal size, so we calm it down.
	const size = name === "info" ? 13 : 14
	if (filled) {
		return (
			<Icon
				name={name}
				size={size}
				strokeWidth={0}
				fill="currentColor" />
		)
	}
	return (
		<Icon
			name={name}
			size={size}
			strokeWidth={2.25} />
	)
}

/**
 * A status badge.
 *
 * Two shapes:
 *  - Static:    { icon, label, variant }                  — always shown
 *  - Lifecycle: { phases: [{ duration, ...phaseShape }] } — auto-advances
 *
 * `phases` is an ordered list. Each entry except the last carries a
 * `duration` (ms). Progression is gated on `activated` — phase 0 is held
 * indefinitely until the parent stage activates. Any leading phases with
 * no `duration` are then skipped (so a `pending` first-phase doesn't
 * flash on activation; it serves as the pre-activation marker only).
 *
 * Variants: default | success | info | loading | pending
 */
function Badge({ badge, activated = true }) {
	const phases = badge.phases || [badge]

	// Initial render: if already activated on mount, skip past any
	// 0-duration leading phases so we don't paint a "pending" frame
	// before transitioning to the real first state.
	const initialIdx = (() => {
		if (!activated) return 0
		let idx = 0
		while (idx < phases.length - 1 && !phases[idx].duration) idx++
		return idx
	})()

	const [phaseIndex, setPhaseIndex] = useState(initialIdx)

	useEffect(() => {
		// Recompute start whenever activation or the badge identity changes.
		let start = 0
		if (activated) {
			while (start < phases.length - 1 && !phases[start].duration) start++
		}
		setPhaseIndex(start)

		if (!activated) return
		if (phases.length <= start + 1) return

		const timeouts = []
		let cumulative = 0
		for (let i = start; i < phases.length - 1; i++) {
			cumulative += phases[i].duration || 0
			const id = setTimeout(() => setPhaseIndex(i + 1), cumulative)
			timeouts.push(id)
		}
		return () => timeouts.forEach(clearTimeout)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [badge, activated])

	const display = phases[phaseIndex]
	const variant = display.variant || "default"
	const cls = ["timeline-trigger-pill", `timeline-trigger-pill--${variant}`].join(" ")

	return (
		<div className={cls}>
			{variant === "loading" ? (
				<span className="timeline-trigger-pill-spinner" />
			) : variant === "pending" ? (
				<span className="timeline-trigger-pill-pending-pip" />
			) : (
				<BadgeIcon name={display.icon} />
			)}
			<span>{display.label}</span>
		</div>
	)
}

function TriggerPill({ trigger, activated = true }) {
	if (!trigger) return null
	const list = Array.isArray(trigger) ? trigger : [trigger]
	if (!list.length) return null
	return (
		<div className="timeline-triggers">
			{list.map((badge, i) => (
				<Badge
					key={badge.id ?? i}
					badge={badge}
					activated={activated} />
			))}
		</div>
	)
}

function ActivityItem({ item }) {
	const cls = ["timeline-activity-row"]
	if (item.status === "active") cls.push("timeline-activity-row--active")
	if (item.status === "pending") cls.push("timeline-activity-row--pending")
	if (item.status === "skipped") cls.push("timeline-activity-row--pending")
	return (
		<li className={cls.join(" ")}>
			<StatusPip status={item.status} />
			<div className="timeline-activity-body">
				<div className="timeline-activity-line">{item.content}</div>
				{item.sub && <div className="timeline-activity-sub">{item.sub}</div>}
			</div>
			{item.duration && <span className="timeline-duration">{item.duration}</span>}
		</li>
	)
}

function Summary({ summary }) {
	if (summary == null) return null
	const parts = Array.isArray(summary) ? summary.filter(Boolean) : [summary]
	if (!parts.length) return null
	return (
		<div className="timeline-summary">
			{parts.map((part, i) => (
				<React.Fragment key={i}>
					{i > 0 && (
						<span
							className="timeline-summary-dot"
							aria-hidden="true">
							·
						</span>
					)}
					<span className="timeline-summary-part">{part}</span>
				</React.Fragment>
			))}
		</div>
	)
}

function ActivityCard({ activity }) {
	const defaultOpen = activity?.status === "active"
	const [open, setOpen] = useState(defaultOpen)

	// Sync to the status-derived default whenever status changes from outside.
	useEffect(() => {
		setOpen(defaultOpen)
	}, [defaultOpen])

	if (!activity) return null
	const meta = activity.meta || {}
	const action = activity.action
	const toggle = () => setOpen((o) => !o)
	const hasItems = (activity.items || []).length > 0

	return (
		<div className="timeline-card timeline-card--activity">
			<button
				type="button"
				className={[
					"timeline-activity-header",
					open && "is-open",
					action && "has-action",
				]
					.filter(Boolean)
					.join(" ")}
				onClick={toggle}>
				<div className="timeline-activity-header-left">
					<StatusPip status={activity.status} />
					<span className="timeline-activity-title">{activity.title}</span>
				</div>
				<div className="timeline-activity-header-right">
					{meta.steps && <span className="timeline-steps">{meta.steps}</span>}
					{meta.elapsed && <span className="timeline-elapsed">{meta.elapsed}</span>}
				</div>
				{hasItems && (
					<span className="timeline-header-chevron-wrap">
						<ChevronDown
							size={14}
							strokeWidth={2}
							className={["timeline-header-chevron", open && "is-up"].filter(Boolean).join(" ")} />
					</span>
				)}
			</button>

			{/* Optional re-run / action affordance — rendered as a sibling (not
			    nested inside the header button) so clicking it doesn't toggle. */}
			{action && (
				<button
					type="button"
					className={["timeline-card-action", action.loading && "is-loading"].filter(Boolean).join(" ")}
					disabled={action.disabled || action.loading}
					aria-label={action.label || "Re-run"}
					onClick={action.onClick}>
					<Icon
						name={action.icon || "rotate-cw"}
						size={13}
						strokeWidth={2} />
				</button>
			)}

			{/* Summary — renders below the header whenever the activity has a
			    summary value and isn't still pending. Always open so any
			    real-time updates are visible alongside the running items list. */}
			{activity.summary != null && activity.status !== "pending" && (
				<div className="timeline-activity-collapse timeline-activity-collapse--summary is-open">
					<div className="timeline-activity-collapse-inner">
						<Summary summary={activity.summary} />
					</div>
				</div>
			)}

			{/* Error — surfaced for failed steps so the reason is visible inline. */}
			{activity.error && (
				<div className="timeline-activity-collapse timeline-activity-collapse--summary is-open">
					<div className="timeline-activity-collapse-inner">
						<div className="timeline-error">{activity.error}</div>
					</div>
				</div>
			)}

			{/* List — visible when expanded. */}
			{hasItems && (
				<div
					className={[
						"timeline-activity-collapse",
						"timeline-activity-collapse--list",
						open && "is-open",
					]
						.filter(Boolean)
						.join(" ")}
					aria-hidden={!open}>
					<div className="timeline-activity-collapse-inner">
						<div className="timeline-activity-divider" />
						<ul className="timeline-activity-list">
							{(activity.items || []).map((item, i) => (
								<ActivityItem
									key={item.id ?? i}
									item={item} />
							))}
						</ul>
					</div>
				</div>
			)}
		</div>
	)
}

function ParallelGroup({ group }) {
	if (!group) return null
	return (
		<div className="timeline-group">
			<div className="timeline-group-label">
				<StatusPip status={group.status} />
				<span className="timeline-group-title">{group.title}</span>
			</div>
			<div className="timeline-group-cards">
				{(group.activities || []).map((sub, i) => (
					<ActivityCard
						key={sub.id ?? i}
						activity={sub} />
				))}
			</div>
		</div>
	)
}

export function Stage({ stage }) {
	const stageStatus = stage.activity?.status ?? stage.group?.status
	const dim = stageStatus === "pending" || stageStatus === "skipped"
	// A stage is "activated" once it leaves pending — at that point its
	// lifecycle badges should start cycling through their phases.
	const activated = stageStatus === "active" || stageStatus === "complete"
	const cls = ["timeline-stage", dim && "timeline-stage--dim"].filter(Boolean).join(" ")
	return (
		<div className={cls}>
			<HeaderCard header={stage.header} />
			{!stage.triggerAfter && (
				<TriggerPill
					trigger={stage.trigger}
					activated={activated} />
			)}
			{stage.activity && <ActivityCard activity={stage.activity} />}
			{stage.group && <ParallelGroup group={stage.group} />}
			{stage.triggerAfter && (
				<TriggerPill
					trigger={stage.trigger}
					activated={activated} />
			)}
		</div>
	)
}

/**
 * Top-level component. Renders the whole timeline from a `{ stages: [...] }`
 * data object. Self-contained: carries its own font + colour root so it can
 * be dropped into any surface (sandbox page, modal, drawer).
 */
function Timeline({ data, className }) {
	const stages = data?.stages || []
	return (
		<div className={["ghd-timeline", className].filter(Boolean).join(" ")}>
			<div className="timeline-stages">
				{stages.map((stage, i) => (
					<Stage
						key={stage.id ?? i}
						stage={stage} />
				))}
			</div>
		</div>
	)
}

export default Timeline

/* ------------------------------------------------------------------ */
/* State helpers — pure mutators returning a new data object.         */
/* Public API for scripted drivers (e.g. the /design demo runner).    */
/* ------------------------------------------------------------------ */

export function setStageStatus(data, stageId, status) {
	return {
		...data,
		stages: data.stages.map((s) => (s.id === stageId ? { ...s, activity: { ...s.activity, status } } : s)),
	}
}
export function setStageMeta(data, stageId, meta) {
	return {
		...data,
		stages: data.stages.map((s) =>
			s.id === stageId ? { ...s, activity: { ...s.activity, meta: { ...(s.activity?.meta || {}), ...meta } } } : s
		),
	}
}
export function setItemStatus(data, stageId, itemIndex, status) {
	return {
		...data,
		stages: data.stages.map((s) => {
			if (s.id !== stageId) return s
			const items = s.activity.items.map((it, i) => (i === itemIndex ? { ...it, status } : it))
			return { ...s, activity: { ...s.activity, items } }
		}),
	}
}
export function setStageSummary(data, stageId, summary) {
	return {
		...data,
		stages: data.stages.map((s) => (s.id === stageId ? { ...s, activity: { ...s.activity, summary } } : s)),
	}
}

export function formatTimestamp(d) {
	const pad = (n) => String(n).padStart(2, "0")
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

export function setGroupStatus(data, stageId, status) {
	return {
		...data,
		stages: data.stages.map((s) => (s.id === stageId ? { ...s, group: { ...s.group, status } } : s)),
	}
}
export function setSubStatus(data, stageId, subIndex, status) {
	return {
		...data,
		stages: data.stages.map((s) => {
			if (s.id !== stageId) return s
			const activities = s.group.activities.map((sub, i) => (i === subIndex ? { ...sub, status } : sub))
			return { ...s, group: { ...s.group, activities } }
		}),
	}
}
export function setSubMeta(data, stageId, subIndex, meta) {
	return {
		...data,
		stages: data.stages.map((s) => {
			if (s.id !== stageId) return s
			const activities = s.group.activities.map((sub, i) =>
				i === subIndex ? { ...sub, meta: { ...(sub.meta || {}), ...meta } } : sub
			)
			return { ...s, group: { ...s.group, activities } }
		}),
	}
}
export function setSubItemStatus(data, stageId, subIndex, itemIndex, status) {
	return {
		...data,
		stages: data.stages.map((s) => {
			if (s.id !== stageId) return s
			const activities = s.group.activities.map((sub, i) => {
				if (i !== subIndex) return sub
				const items = sub.items.map((it, k) => (k === itemIndex ? { ...it, status } : it))
				return { ...sub, items }
			})
			return { ...s, group: { ...s.group, activities } }
		}),
	}
}

/**
 * Sum of the non-final phase durations across any `block: true` lifecycle
 * badges in a trigger. Multiple blocking badges → take the max so items wait
 * for the slowest gate to clear.
 */
export function computeBadgeBlockDelay(trigger) {
	if (!trigger) return 0
	const list = Array.isArray(trigger) ? trigger : [trigger]
	let maxDelay = 0
	for (const badge of list) {
		if (!badge.block) continue
		const phases = badge.phases || [badge]
		let total = 0
		for (let i = 0; i < phases.length - 1; i++) {
			total += phases[i].duration || 0
		}
		if (total > maxDelay) maxDelay = total
	}
	return maxDelay
}
